import React, { useEffect, useState } from 'react';
import { Box, Popover, Typography, Button } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { useSettings } from '../../providers/settings-provider';

type DateRangeFilterProps = {
	start: Date;
	end: Date;
	onChange?: (start: Date, end: Date) => void;
	separator?: string;
	sx?: React.CSSProperties | undefined;
	// Optional quick-set buttons, shown in the popover below the calendar
	// when provided - lets the caller snap the start/end to the edges of
	// whatever dataset it's filtering (e.g. the earliest/latest record
	// among the current candidates), same as the Transactions page's own
	// Earliest/Latest Record buttons.
	earliestDate?: Date | null;
	latestDate?: Date | null;
	// Overrides the quick-set buttons' default "Earliest/Latest Record" text -
	// lets a caller say what's actually being snapped to (e.g. "Earliest
	// Provision" when the candidate list is provisions-only).
	earliestLabel?: string;
	latestLabel?: string;
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ start, end, onChange, sx, separator, earliestDate, latestDate, earliestLabel = 'Earliest Record', latestLabel = 'Latest Record' }) => {
	const { formatDate } = useSettings();
	const [activeField, setActiveField] = useState<'start' | 'end'>('start');
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [localStart, setLocalStart] = useState<Date>(start);
	const [localEnd, setLocalEnd] = useState<Date>(end);

	useEffect(() => {
		setLocalStart(start);
		setLocalEnd(end);
	}, [start, end]);

	const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(e.currentTarget);
	};
	const handleClose = () => {
		// Reset local state to original values when canceling
		setLocalStart(start);
		setLocalEnd(end);
		setAnchorEl(null);
	};

	const handleConfirm = () => {
		setAnchorEl(null);
		if (onChange) onChange(localStart, localEnd);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<Box
				sx={{
					...sx,
					display: 'flex',
					alignItems: 'center',
					border: '1px solid #c7c7c7ff',
					borderRadius: sx?.borderRadius ?? 2,
					px: 2,
					py: 0.5,
					cursor: 'pointer',
					background: 'var(--color-page-background)',
					height: sx?.height ?? 30,
					minHeight: sx?.minHeight ?? 30,
					outline: anchorEl ? '2px solid var(--color-primary)' : 'none',
					boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
					justifyContent: 'space-between',
					'&:hover': { borderColor: '#212121ff' }
				}}
				onClick={handleOpen}
			>
				<Typography fontSize={sx?.fontSize ?? 14} sx={{ textWrap: 'nowrap' }} fontWeight={400} color="var(--color-default-text)">
					{formatDate(localStart)}
				</Typography>
				<Typography fontSize={sx?.fontSize ?? 14} fontWeight={400} color="var(--color-default-text)" mx={1}>
					{separator ?? '-'}
				</Typography>
				<Typography fontSize={sx?.fontSize ?? 14} sx={{ textWrap: 'nowrap' }} fontWeight={400} color="var(--color-default-text)">
					{formatDate(localEnd)}
				</Typography>
			</Box>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				PaperProps={{ sx: { borderRadius: 2, minWidth: 340 } }}
			>
				<Box display="flex" flexDirection="column">
					<Box display="flex" gap={1} pt={2} px={2} pb={earliestDate || latestDate ? 0.25 : 2}>
						<Box sx={{ width: '50%' }}>
							<Typography fontSize={14} color='var(--color-text-muted)'>Start date</Typography>
							<Box
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ cursor: 'pointer', border: activeField === 'start' ? '2px solid var(--color-primary)' : '1px solid #E0E3E6', borderRadius: 2, p: 0.5, background: 'var(--color-page-background)' }}
								onClick={() => setActiveField('start')}
							>
								<Typography fontSize={14} color="var(--color-default-text)" p={0.5}>
									{formatDate(localStart)}
								</Typography>
							</Box>
						</Box>
						<Box sx={{ width: '50%' }}>
							<Typography fontSize={14} color='var(--color-text-muted)'>End date</Typography>
							<Box
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ cursor: 'pointer', border: activeField === 'end' ? '2px solid var(--color-primary)' : '1px solid #E0E3E6', borderRadius: 2, p: 0.5, background: 'var(--color-page-background)' }}
								onClick={() => setActiveField('end')}
							>
								<Typography fontSize={14} color="var(--color-default-text)" p={0.5}>
									{formatDate(localEnd)}
								</Typography>
							</Box>
						</Box>
					</Box>
					{(earliestDate || latestDate) && (
						<Box display="flex" gap={1} px={2} pb={2}>
							<Button
								size="small"
								variant="outlined"
								fullWidth
								sx={{
									height: 24,
									fontSize: 10,
									textTransform: 'none',
									borderColor: '#E0E0E0',
									color: 'var(--color-text-muted)',
									'&:hover': { borderColor: 'var(--color-primary)', backgroundColor: '#F0F9F7' }
								}}
								onClick={() => {
									if (earliestDate) setLocalStart(earliestDate);
								}}
								disabled={!earliestDate}
							>
								{earliestLabel}
							</Button>
							<Button
								size="small"
								variant="outlined"
								fullWidth
								sx={{
									height: 24,
									fontSize: 10,
									textTransform: 'none',
									borderColor: '#E0E0E0',
									color: 'var(--color-text-muted)',
									'&:hover': { borderColor: 'var(--color-primary)', backgroundColor: '#F0F9F7' }
								}}
								onClick={() => {
									if (latestDate) setLocalEnd(latestDate);
								}}
								disabled={!latestDate}
							>
								{latestLabel}
							</Button>
						</Box>
					)}
					<Box>
						<StaticDatePicker
							displayStaticWrapperAs="desktop"
							value={activeField === 'start' ? localStart : localEnd}
							onChange={date => {
								if (activeField === 'start') {
									if (date) {
										const newStartDate = date instanceof Date ? date : new Date(date as any);
										setLocalStart(newStartDate);
										// If new start date is after end date, adjust end date
										if (newStartDate > localEnd) {
											setLocalEnd(newStartDate);
										}
									}
								} else {
									if (date) {
										const newEndDate = date instanceof Date ? date : new Date(date as any);
										// Only set end date if it's not before start date
										if (newEndDate >= localStart) {
											setLocalEnd(newEndDate);
										}
									}
								}
							}}
							slots={{ actionBar: () => null }}
							minDate={activeField === 'end' ? localStart : undefined}
						/>
					</Box>
					<Box display="flex" justifyContent="space-between" gap={1} p={2} pt={0}>
						<Button 
							variant="outlined" 
							size="small"
							onClick={handleClose}
							sx={{ 
								minWidth: 80,
								textTransform: 'none'
							}}
						>
							Cancel
						</Button>
						<Button 
							variant="contained" 
							size="small"
							onClick={handleConfirm}
							sx={{ 
								minWidth: 80,
								backgroundColor: 'var(--color-primary)',
								color: '#fff',
								textTransform: 'none',
								'&:hover': {
									backgroundColor: '#009688',
									color: '#fff'
								}
							}}
						>
							Apply
						</Button>
					</Box>
				</Box>
			</Popover>
		</LocalizationProvider>
	);
};

export default DateRangeFilter;
